import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Flame } from "lucide-react";
import { useCountry } from "@/lib/CountryContext";
import { fetchLatestAnnonce } from "@/lib/api";
import { fmtPrice } from "@/lib/utils";

// Sessionn-storage key prefix: throttle the toast to once per browser session
// per country, so users aren't bombarded.
const SESSION_KEY = "fodindin:freshAnnonceToast";

function getShownThisSession(code, annonceId) {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return false;
    const map = JSON.parse(raw);
    return map[code] === annonceId;
  } catch { return false; }
}

function markShownThisSession(code, annonceId) {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[code] = annonceId;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(map));
  } catch { /* ignore */ }
}

/**
 * Headless hook: on mount, fetches the most recent annonce (<24h) for the
 * active country and surfaces a clickable "🔥 New" toast.
 *
 * - Throttled to once per browser session per country (avoids spam).
 * - Skipped if no annonce in the window (backend returns 204 → null).
 * - Delayed ~1.2s after mount so it doesn't fight with first paint.
 */
export default function useFreshAnnonceToast() {
  const navigate = useNavigate();
  const { code, country } = useCountry();

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    timer = setTimeout(async () => {
      const ann = await fetchLatestAnnonce(code, 24);
      if (cancelled || !ann || !ann.id) return;
      if (getShownThisSession(code, ann.id)) return;
      markShownThisSession(code, ann.id);
      toast(
        `Nouveauté ${country.flag} ${ann.city} — ${ann.category || "Annonce"}`,
        {
          description: `${ann.title} · ${fmtPrice(ann.price, ann.price_unit)}`,
          icon: <Flame size={18} className="text-orange-500" />,
          duration: 7000,
          action: {
            label: "Voir",
            onClick: () => navigate(`/annonces/${ann.id}`),
          },
          className: `fresh-annonce-toast-${code}`,
        }
      );
    }, 1200);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Re-run when the country changes so users get the toast for the new market.
  }, [code, country.name, country.flag, navigate]);
}
